/**
 * Three.js scene wrapper.
 *
 * Replaces the Phase 1 stub. Adds:
 *  - GLB loading for the part (one mesh per face, raycast-pickable)
 *  - Toolpath polylines (one per operation, with arrow glyphs)
 *  - Face selection highlighting driven by store.selection + operation.faces
 *  - A corner view cube (small, bottom-right)
 *  - Scroll-wheel zoom via OrbitControls
 *
 * Scene coordinates match amatl_core: millimetres, Z up.
 */
import * as THREE from 'three'
import { GLTFLoader, OrbitControls, STLLoader } from 'three-stdlib'
import URDFLoader from 'urdf-loader'
import type { URDFRobot } from 'urdf-loader'

import type { Job, Operation, ToolDefinition } from '../store'

/**
 * ABB IRB 4600-40/2.55 URDF joint origins (parent-link frame), in METERS.
 * Mirrors amatl_core/fk/link_transforms.py:JOINT_SPECS exactly. The axes
 * are only needed when posing non-zero joint angles; at home (q = 0) each
 * link transform is the cumulative sum of origins, so we skip axis math.
 */
const IRB4600_JOINT_ORIGINS_M: [number, number, number][] = [
  [0.000, 0, 0.495],
  [0.175, 0, 0.000],
  [0.000, 0, 1.095],
  [0.000, 0, 0.175],
  [1.270, 0, 0.000],
  [0.135, 0, 0.000],
]

const ROBOT_LINK_URLS = [
  '/robot_assets/meshes_irb4600_40_255/base_link.stl',
  '/robot_assets/meshes_irb4600_40_255/link_1.stl',
  '/robot_assets/meshes_irb4600_40_255/link_2.stl',
  '/robot_assets/meshes_irb4600_40_255/link_3.stl',
  '/robot_assets/meshes_irb4600_40_255/link_4.stl',
  '/robot_assets/meshes_irb4600_40_255/link_5.stl',
  '/robot_assets/meshes_irb4600_40_255/link_6.stl',
]

export type ViewPreset = 'iso' | 'top' | 'right' | 'front'
export type PickCallback = (faceIndex: number) => void

/** The subset of a cell definition the viewport needs to draw the
 * positioner. Matches the fields in `api.CellDefinition` so callers
 * can pass that type directly. All lengths in millimetres, angles in
 * degrees, consistent with the backend. */
export type ViewportCell = {
  robot?: { position_mm?: number[] }
  positioner: {
    base_position_mm: number[]
    base_orientation_deg?: number[]
    first_axis_in_base: number[]
    faceplate_offset_mm: number[]
    // Phase 2D: visualize the tilt. Non-empty tilt_mech_unit_name
    // means the cell has a 2-axis positioner — rotate the mesh by
    // tilt_angle_deg around tilt_axis_in_base after base placement.
    tilt_mech_unit_name?: string
    tilt_axis_in_base?: number[]
    tilt_angle_deg?: number
  }
  work_object?: {
    offset_mm?: number[]
    // Direction of the worm's long axis in the faceplate frame.
    // Default (0, 0, 1) = long axis along faceplate +Z (the rotation
    // axis). Set e.g. (1, 0, 0) for a part mounted sideways.
    worm_axis_in_faceplate?: number[]
  }
}

/** Default placement matches the demo_cell shipped with AMATL. */
const DEFAULT_CELL: ViewportCell = {
  robot: { position_mm: [0, 0, 0] },
  positioner: {
    base_position_mm: [1600, 0, 0],
    base_orientation_deg: [0, 0, 0],
    first_axis_in_base: [1, 0, 0],
    faceplate_offset_mm: [0, 0, 1100],
  },
  work_object: { offset_mm: [0, 0, 0] },
}

export class Scene {
  private host: HTMLElement
  private renderer: THREE.WebGLRenderer
  private scene: THREE.Scene
  private camera: THREE.PerspectiveCamera
  private controls: OrbitControls
  private partGroup: THREE.Group
  private pathGroup: THREE.Group
  private faceMeshes: Map<number, THREE.Mesh> = new Map()
  private running = false
  private ro: ResizeObserver
  private frameId = 0
  private pickCb: PickCallback | null = null
  private currentPartUrl: string | null = null
  private raycaster = new THREE.Raycaster()
  private pointer = new THREE.Vector2()
  private _grid!: THREE.GridHelper
  private robotGroup: THREE.Group
  private positionerGroup: THREE.Group
  private toolGroup: THREE.Group
  private currentToolSig: string | null = null
  private currentCell: ViewportCell = DEFAULT_CELL

  constructor(host: HTMLElement, options: { skipRobotMesh?: boolean } = {}) {
    this.host = host

    this.renderer = new THREE.WebGLRenderer({ antialias: true })
    this.renderer.setPixelRatio(window.devicePixelRatio)
    host.appendChild(this.renderer.domElement)

    this.scene = new THREE.Scene()

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x404040, 0.9))
    const dir = new THREE.DirectionalLight(0xffffff, 0.6)
    dir.position.set(1500, 2000, 1500)
    this.scene.add(dir)

    const grid = new THREE.GridHelper(2000, 20, 0x4a5260, 0x30363d)
    grid.rotation.x = Math.PI / 2 // XY ground; Z-up world
    this.scene.add(grid)
    this._grid = grid

    this.setTheme('dark')

    const triad = new THREE.AxesHelper(200)
    this.scene.add(triad)

    this.partGroup = new THREE.Group()
    this.scene.add(this.partGroup)
    this.pathGroup = new THREE.Group()
    this.scene.add(this.pathGroup)

    this.robotGroup = new THREE.Group()
    this.robotGroup.name = 'robot_IRB4600'
    this.scene.add(this.robotGroup)
    this.positionerGroup = new THREE.Group()
    this.positionerGroup.name = 'positioner_IRBP'
    this.scene.add(this.positionerGroup)
    this.toolGroup = new THREE.Group()
    this.toolGroup.name = 'tool'
    // Mount the tool at the IRB 4600 flange centre at home pose:
    // cumulative sum of URDF origins at q=0 = (1580, 0, 1765) mm.
    this.toolGroup.position.set(1580, 0, 1765)
    this.scene.add(this.toolGroup)
    if (!options.skipRobotMesh) {
      // Try the URDF path first (preferred — kinematic chain + mesh set
      // in one standard file). Falls back to the hand-coded STL loader
      // if the URDF endpoint is unreachable (older server, offline dev),
      // so the viewport is never empty just because the asset registry
      // round-trip failed.
      this.loadRobotURDF('ABB_IRB4600_40_255').catch(() => {
        console.warn('URDF load failed — falling back to STL home pose')
        return this.buildRobotHomePose()
      })
    }
    this.buildPositioner()

    this.camera = new THREE.PerspectiveCamera(45, 1, 1, 20000)
    this.camera.up.set(0, 0, 1)
    this.camera.position.set(3800, -4200, 2800)
    this.camera.lookAt(800, 0, 1000)

    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.target.set(800, 0, 1000)
    this.controls.enableDamping = true

    this.renderer.domElement.addEventListener('click', this.onClick)

    this.ro = new ResizeObserver(() => this.resize())
    this.ro.observe(host)
    this.resize()
  }

  // ---- lifecycle --------------------------------------------------------

  private resize() {
    const w = this.host.clientWidth || 1
    const h = this.host.clientHeight || 1
    this.renderer.setSize(w, h, false)
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
  }

  start() {
    this.running = true
    const loop = () => {
      if (!this.running) return
      this.controls.update()
      this.renderer.render(this.scene, this.camera)
      this.frameId = requestAnimationFrame(loop)
    }
    this.frameId = requestAnimationFrame(loop)
  }

  dispose() {
    this.running = false
    cancelAnimationFrame(this.frameId)
    this.ro.disconnect()
    this.renderer.domElement.removeEventListener('click', this.onClick)
    this.renderer.dispose()
    if (this.renderer.domElement.parentNode === this.host) {
      this.host.removeChild(this.renderer.domElement)
    }
  }

  // ---- picking ----------------------------------------------------------

  setPickCallback(cb: PickCallback | null) {
    this.pickCb = cb
  }

  private onClick = (e: MouseEvent) => {
    if (!this.pickCb) return
    const rect = this.renderer.domElement.getBoundingClientRect()
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    this.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    this.raycaster.setFromCamera(this.pointer, this.camera)
    const hits = this.raycaster.intersectObjects(Array.from(this.faceMeshes.values()), false)
    if (hits.length > 0) {
      const idx = (hits[0].object.userData.faceIndex as number) ?? -1
      if (idx >= 0) this.pickCb(idx)
    }
  }

  // ---- robot + positioner ----------------------------------------------

  /** URDF-driven robot loader. Fetches `/api/robots/{id}` to find the
   * URDF URL, lets urdf-loader build the full kinematic tree + mesh set,
   * scales to the scene's millimetre units, and places it under
   * ``robotGroup``. Replaces ``buildRobotHomePose`` for every robot in
   * the asset registry — the STL loader stays only as a fallback for
   * offline dev. */
  private robotURDF: URDFRobot | null = null

  private async loadRobotURDF(robotId: string): Promise<void> {
    // 1. Discover URL. `/api/robots/{id}` returns {urdf_url, …}; a 404
    //    means this robot isn't in the registry and we fall back.
    const r = await fetch(`/api/robots/${robotId}`)
    if (!r.ok) throw new Error(`no robot registry entry for ${robotId}`)
    const info = (await r.json()) as { urdf_url: string; urdf_present: boolean }
    if (!info.urdf_present) throw new Error(`URDF for ${robotId} not on disk`)

    // 2. Parse. urdf-loader resolves mesh paths relative to the URDF's
    //    own directory, which matches the sibling-STL layout we ship.
    //    STL loads are async; a LoadingManager tells us when they're
    //    all in so we can apply the material override after.
    const mat = new THREE.MeshStandardMaterial({
      color: 0xd97706,  // ABB orange
      metalness: 0.25,
      roughness: 0.55,
    })
    const manager = new THREE.LoadingManager()
    const loader = new URDFLoader(manager)
    loader.parseVisual = true
    loader.parseCollision = false
    const robot = await loader.loadAsync(info.urdf_url)
    // The URDF tree exists synchronously but mesh content fills in async.
    // Wait for every mesh file to load, then paint.
    await new Promise<void>((resolve) => {
      manager.onLoad = () => resolve()
      // No pending loads → onLoad never fires. Guard with a short tick.
      setTimeout(() => resolve(), 2000)
    })
    robot.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) {
        (obj as THREE.Mesh).material = mat
      }
    })

    // 3. Place. URDFs are in METRES; the scene is in MILLIMETRES. One
    //    uniform scale on the parent group matches our hand-coded STL
    //    loader's (mesh.scale × 1000) + (position × 1000) convention.
    //    Z-up matches ROS convention — no rotation needed.
    robot.scale.setScalar(1000)

    // 5. Stash + mount. Keep a handle so later setRobotJointValues()
    //    can pose it; replace any previous robot (reload-safe).
    while (this.robotGroup.children.length) {
      const c = this.robotGroup.children[0]
      this.robotGroup.remove(c)
      disposeTree(c)
    }
    this.robotURDF = robot
    this.robotGroup.add(robot)
  }

  /** Set the robot's 6 joint angles (radians). No-op if the URDF hasn't
   * loaded yet or we're on the STL fallback. Order matches the URDF's
   * joint_1..joint_6. */
  setRobotJointValues(joints: number[]): void {
    if (!this.robotURDF) return
    for (let i = 0; i < 6; i++) {
      const name = `joint_${i + 1}`
      if (this.robotURDF.joints[name]) {
        this.robotURDF.setJointValue(name, joints[i] ?? 0)
      }
    }
  }

  /** Hand-coded STL home-pose loader, retained as a fallback for when
   * the URDF endpoint is unreachable. The STLs are in METERS in their
   * own link frame; the scene uses MILLIMETRES so each mesh gets
   * scale 1000 and each link's position is the cumulative sum of
   * parent-origin translations × 1000 mm. */
  private async buildRobotHomePose() {
    const loader = new STLLoader()
    // Cumulative link origin (mm) in robot RCF — matches _link_transforms
    // in amatl_core/fk/link_transforms.py at joints=[0,0,0,0,0,0].
    const cumulative: [number, number, number][] = [[0, 0, 0]]
    let x = 0, y = 0, z = 0
    for (const [ox, oy, oz] of IRB4600_JOINT_ORIGINS_M) {
      x += ox * 1000
      y += oy * 1000
      z += oz * 1000
      cumulative.push([x, y, z])
    }

    const mat = new THREE.MeshStandardMaterial({
      color: 0xd97706, // ABB orange
      metalness: 0.25,
      roughness: 0.55,
    })

    await Promise.all(
      ROBOT_LINK_URLS.map(async (url, i) => {
        try {
          const geom = await loader.loadAsync(url)
          const mesh = new THREE.Mesh(geom, mat)
          mesh.scale.setScalar(1000) // m → mm
          mesh.position.set(...cumulative[i])
          mesh.name = `robot_link_${i}`
          this.robotGroup.add(mesh)
        } catch (err) {
          console.warn('STL load failed for', url, err)
        }
      }),
    )
  }

  /** URDF-driven positioner. Loads the Yaskawa MotoPos MH1655 URDF
   * from the asset registry (ABB IRBP has no public URDF; this is the
   * same single-horizontal-shaft kinematic class). Scaled m→mm,
   * positioned/oriented from the cell. The parametric faceplate disc
   * is still drawn as a *kinematic marker* at ``faceplate_offset_mm``
   * so the user can see where the part will land, even if the URDF's
   * physical flange doesn't match the cell's defined faceplate. */
  private positionerURDF: URDFRobot | null = null
  private positionerLoadInFlight: Promise<void> | null = null

  private async loadPositionerURDF(positionerId: string): Promise<void> {
    const r = await fetch(`/api/positioners/${positionerId}`)
    if (!r.ok) throw new Error(`no positioner asset for ${positionerId}`)
    const info = (await r.json()) as { urdf_url: string; urdf_present: boolean }
    if (!info.urdf_present) throw new Error(`URDF for ${positionerId} not on disk`)

    const mat = new THREE.MeshStandardMaterial({
      color: 0x4a7fc8, // Yaskawa blue
      metalness: 0.25,
      roughness: 0.55,
    })
    const manager = new THREE.LoadingManager()
    const loader = new URDFLoader(manager)
    loader.parseVisual = true
    loader.parseCollision = false
    const robot = await loader.loadAsync(info.urdf_url)
    await new Promise<void>((resolve) => {
      manager.onLoad = () => resolve()
      setTimeout(() => resolve(), 2000)
    })
    robot.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) {
        (obj as THREE.Mesh).material = mat
      }
    })
    robot.scale.setScalar(1000) // m → mm, same convention as the robot
    this.positionerURDF = robot
  }

  /** Build the procedural positioner. Preferred path: load the URDF
   * mesh set (Yaskawa MotoPos MH1655) and add the kinematic faceplate
   * marker on top. Fallback: pure parametric (box pedestal + cylinder
   * shaft + disc) when the URDF endpoint is unreachable. */
  private buildPositioner() {
    // Clear whatever was there.
    while (this.positionerGroup.children.length) {
      const c = this.positionerGroup.children[0]
      this.positionerGroup.remove(c)
      disposeTree(c)
    }

    const cell = this.currentCell

    // Kick off URDF load once per Scene lifetime; subsequent builds
    // (keystroke-triggered) re-add the already-loaded mesh.
    if (!this.positionerURDF && !this.positionerLoadInFlight) {
      const modelId = cell.positioner?.base_position_mm
        ? 'IRBP_A250_D1000'  // Scene doesn't carry model_id yet; the
                             // registry aliases this to the Yaskawa.
        : null
      if (modelId) {
        this.positionerLoadInFlight = this.loadPositionerURDF(modelId)
          .then(() => { this.buildPositioner() })
          .catch(() => { this.buildPositionerParametric() })
          .finally(() => { this.positionerLoadInFlight = null })
      } else {
        this.buildPositionerParametric()
        return
      }
    }

    if (this.positionerURDF) {
      // URDF path: add the mesh at the cell's base pose. The D500's own
      // mesh shows its physical faceplate on link_2, so no extra marker
      // is drawn — that was only there for the MH1655 era when the
      // mesh didn't match the IRBP A-250 default faceplate_offset_mm.
      this.positionerGroup.add(this.positionerURDF)
    } else {
      // Load still pending — leave group empty for this frame; the
      // completion handler will call buildPositioner() again.
      return
    }

    this.applyPositionerPlacement()
  }

  /** Pose ``positionerGroup`` from the cell's base + tilt. Shared
   * between the URDF and parametric paths so Phase 2D tilt viz lands
   * in one place. */
  private applyPositionerPlacement() {
    const cell = this.currentCell
    const base = cell.positioner.base_position_mm
    this.positionerGroup.position.set(base[0], base[1], base[2])
    const baseOri = cell.positioner.base_orientation_deg ?? [0, 0, 0]
    // Base orientation (Euler) then an extra tilt around
    // tilt_axis_in_base when the cell has a 2-axis positioner. We use
    // a quaternion so the tilt stacks on top of the Euler base
    // orientation without re-decomposing into intrinsic angles.
    const baseQuat = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(
        THREE.MathUtils.degToRad(baseOri[0]),
        THREE.MathUtils.degToRad(baseOri[1]),
        THREE.MathUtils.degToRad(baseOri[2]),
        'XYZ',
      ),
    )
    // Phase 2D (D500 swap): when the loaded URDF has a joint_1 (the
    // D500 tilt joint), drive it with the cell's tilt_angle_deg so the
    // tilt HOUSING moves while the base stays put. Falls back to the
    // whole-group tilt below for URDFs that don't articulate (MH1655,
    // parametric). Multiply the tilt into the base quaternion only in
    // the fallback case.
    const hasArticulatedTilt =
      this.positionerURDF != null &&
      this.positionerURDF.joints != null &&
      'joint_1' in this.positionerURDF.joints
    if (cell.positioner.tilt_mech_unit_name &&
        cell.positioner.tilt_angle_deg != null) {
      if (hasArticulatedTilt && this.positionerURDF) {
        this.positionerURDF.setJointValue(
          'joint_1',
          THREE.MathUtils.degToRad(cell.positioner.tilt_angle_deg),
        )
      } else {
        const tAxis = cell.positioner.tilt_axis_in_base ?? [0, 1, 0]
        const tiltQuat = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(tAxis[0], tAxis[1], tAxis[2]).normalize(),
          THREE.MathUtils.degToRad(cell.positioner.tilt_angle_deg),
        )
        baseQuat.multiply(tiltQuat)
      }
    } else if (hasArticulatedTilt && this.positionerURDF) {
      // Make sure a tilt from a previous frame doesn't linger when the
      // cell switches back to a 1-axis (or zero-tilt) configuration.
      this.positionerURDF.setJointValue('joint_1', 0)
    }
    this.positionerGroup.quaternion.copy(baseQuat)
  }

  /** Parametric fallback — the pre-URDF look. Kept as the dev-offline
   * safety net so a bad network doesn't blank the positioner. */
  private buildPositionerParametric() {
    while (this.positionerGroup.children.length) {
      const c = this.positionerGroup.children[0]
      this.positionerGroup.remove(c)
      disposeTree(c)
    }
    const cell = this.currentCell
    const faceOff = cell.positioner.faceplate_offset_mm
    const firstAxis = normalize3(cell.positioner.first_axis_in_base)
    const matGrey = new THREE.MeshStandardMaterial({ color: 0x808590, metalness: 0.3, roughness: 0.55 })
    const matYellow = new THREE.MeshStandardMaterial({ color: 0xf6c200, metalness: 0.2, roughness: 0.6 })
    const SHAFT_LEN = 220, SHAFT_R = 50, FACE_R = 180, FACE_T = 20
    const shaftEntry: [number, number, number] = [
      faceOff[0] - firstAxis[0] * SHAFT_LEN,
      faceOff[1] - firstAxis[1] * SHAFT_LEN,
      faceOff[2] - firstAxis[2] * SHAFT_LEN,
    ]
    const pedH = Math.max(0, shaftEntry[2])
    if (pedH > 50) {
      const pedW = Math.max(300, Math.min(480, pedH * 0.35))
      const base = new THREE.Mesh(new THREE.BoxGeometry(pedW, pedW, pedH), matYellow)
      base.position.set(shaftEntry[0], shaftEntry[1], pedH / 2)
      this.positionerGroup.add(base)
    }
    const head = new THREE.Mesh(new THREE.BoxGeometry(260, 300, 260), matYellow)
    head.position.set(shaftEntry[0], shaftEntry[1], shaftEntry[2])
    this.positionerGroup.add(head)
    const yUp = new THREE.Vector3(0, 1, 0)
    const shaftVec = new THREE.Vector3(firstAxis[0], firstAxis[1], firstAxis[2])
    const q = new THREE.Quaternion().setFromUnitVectors(yUp, shaftVec)
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(SHAFT_R, SHAFT_R, SHAFT_LEN, 24), matGrey)
    shaft.position.set(
      (shaftEntry[0] + faceOff[0]) / 2,
      (shaftEntry[1] + faceOff[1]) / 2,
      (shaftEntry[2] + faceOff[2]) / 2,
    )
    shaft.quaternion.copy(q)
    this.positionerGroup.add(shaft)
    const face = new THREE.Mesh(new THREE.CylinderGeometry(FACE_R, FACE_R, FACE_T, 48), matGrey)
    face.position.set(faceOff[0], faceOff[1], faceOff[2])
    face.quaternion.copy(q)
    this.positionerGroup.add(face)
    this.applyPositionerPlacement()
  }

  /** Public API: switch the viewport to a new cell geometry. Rebuilds
   * the positioner from the cell's base pose, first axis and faceplate
   * offset. Cheap to call on every form keystroke in the Cell editor. */
  setCell(cell: ViewportCell) {
    this.currentCell = cell
    this.buildPositioner()
    this.recenterPartOnFaceplate()
  }

  /** Compute the faceplate's position in world (robot RCF) coords from
   * the current cell + work-object offset, then re-centre any loaded
   * part on it. Called whenever the cell changes so the worm follows
   * the positioner. Also applies the worm-axis orientation so the mesh
   * visually matches what the engine uses for toolpaths. */
  private recenterPartOnFaceplate() {
    if (!this.partGroup.children.length) return
    this.applyPartOrientation()
    const [x, y, z] = this.faceplatePositionInRcf()
    this.centerPartOn(x, y, z)
  }

  /** Rotate ``partGroup`` so the STEP's local +Z (standard CAD
   * convention for the long axis of a worm/auger) aligns with the
   * worm's long axis in robot frame — the same direction the engine
   * uses for toolpath generation. Mirrors the Python
   * ``CellDefinition.to_cell_config()`` basis construction in
   * amatl_core/cells/schemas.py so what the user sees matches what
   * the generator/IK/RAPID emitter consume. */
  private applyPartOrientation() {
    const cell = this.currentCell
    const firstAxis = normalize3(cell.positioner.first_axis_in_base ?? [1, 0, 0])
    const baseOri = cell.positioner.base_orientation_deg ?? [0, 0, 0]
    const R_base = new THREE.Matrix4().makeRotationFromEuler(
      new THREE.Euler(
        THREE.MathUtils.degToRad(baseOri[0]),
        THREE.MathUtils.degToRad(baseOri[1]),
        THREE.MathUtils.degToRad(baseOri[2]),
        'XYZ',
      ),
    )
    const shaft = new THREE.Vector3(firstAxis[0], firstAxis[1], firstAxis[2])
      .applyMatrix4(R_base).normalize()

    const wormFP = cell.work_object?.worm_axis_in_faceplate ?? [0, 0, 1]
    const isDefault =
      Math.abs(wormFP[0]) < 1e-6 &&
      Math.abs(wormFP[1]) < 1e-6 &&
      Math.abs(wormFP[2] - 1) < 1e-6
    let target: THREE.Vector3
    if (isDefault) {
      // Default: worm long axis = faceplate +Z = shaft direction.
      target = shaft.clone()
    } else {
      // General: build a faceplate basis with +Z along shaft, then map
      // worm_axis_in_faceplate from faceplate coords into robot frame.
      // Picks world +X as the +X reference unless shaft is near-parallel
      // to +X (then falls back to +Y), identical to the Python helper.
      const worldX = new THREE.Vector3(1, 0, 0)
      const dotSX = shaft.dot(worldX)
      let fp_x: THREE.Vector3
      if (Math.abs(dotSX) > 0.99) {
        fp_x = new THREE.Vector3(0, 1, 0)
      } else {
        fp_x = worldX.clone().sub(shaft.clone().multiplyScalar(dotSX)).normalize()
      }
      const fp_z = shaft
      const fp_y = new THREE.Vector3().crossVectors(fp_z, fp_x)
      target = new THREE.Vector3()
        .addScaledVector(fp_x, wormFP[0])
        .addScaledVector(fp_y, wormFP[1])
        .addScaledVector(fp_z, wormFP[2])
        .normalize()
    }
    // Shortest-arc rotation from mesh +Z to the target direction.
    const q = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 0, 1), target,
    )
    this.partGroup.quaternion.copy(q)
  }

  private faceplatePositionInRcf(): [number, number, number] {
    // Compose: base_position + R(base_ori) · (faceplate_offset + work_object_offset)
    const cell = this.currentCell
    const base = cell.positioner.base_position_mm
    const face = cell.positioner.faceplate_offset_mm
    const wo = cell.work_object?.offset_mm ?? [0, 0, 0]
    const local = new THREE.Vector3(face[0] + wo[0], face[1] + wo[1], face[2] + wo[2])
    const baseOri = cell.positioner.base_orientation_deg ?? [0, 0, 0]
    const euler = new THREE.Euler(
      THREE.MathUtils.degToRad(baseOri[0]),
      THREE.MathUtils.degToRad(baseOri[1]),
      THREE.MathUtils.degToRad(baseOri[2]),
      'XYZ',
    )
    local.applyEuler(euler)
    return [base[0] + local.x, base[1] + local.y, base[2] + local.z]
  }

  // ---- theme ------------------------------------------------------------

  setTheme(mode: 'dark' | 'light') {
    const bg = mode === 'dark' ? 0x1c2127 : 0xffffff
    const gridMain = mode === 'dark' ? 0x4a5260 : 0xc1c7cd
    const gridSub = mode === 'dark' ? 0x30363d : 0xdde2e6
    this.renderer.setClearColor(bg)
    this.scene.background = new THREE.Color(bg)
    // GridHelper doesn't expose colors as live attributes; swap the
    // whole helper to pick up the new palette.
    const idx = this.scene.children.indexOf(this._grid)
    if (idx >= 0) {
      const newGrid = new THREE.GridHelper(2000, 20, gridMain, gridSub)
      newGrid.rotation.x = Math.PI / 2
      this.scene.children[idx] = newGrid
      this._grid.geometry.dispose()
      ;(this._grid.material as THREE.Material).dispose()
      this._grid = newGrid
    }
  }

  // ---- view presets -----------------------------------------------------

  view(p: ViewPreset) {
    const r = 1800
    switch (p) {
      case 'iso':
        this.camera.position.set(r * 0.7, -r * 0.9, r * 0.7)
        break
      case 'top':
        this.camera.position.set(0, 0, r)
        break
      case 'right':
        this.camera.position.set(r, 0, 300)
        break
      case 'front':
        this.camera.position.set(0, -r, 300)
        break
    }
    this.controls.target.set(0, 0, 300)
    this.controls.update()
  }

  zoomToFit() {
    // Fit the loaded part if any, otherwise fit the robot + positioner
    // (the user wants F to re-centre the whole cell before any STEP).
    const target = this.partGroup.children.length ? this.partGroup : this.scene
    const box = new THREE.Box3().setFromObject(target)
    if (box.isEmpty()) return
    const c = box.getCenter(new THREE.Vector3())
    const s = box.getSize(new THREE.Vector3())
    const r = Math.max(s.x, s.y, s.z)
    this.camera.position.set(c.x + r, c.y - r * 1.2, c.z + r)
    this.controls.target.copy(c)
    this.controls.update()
  }

  // ---- sync from store --------------------------------------------------

  async syncJob(job: Job) {
    const url = job.part?.tessellation_url ?? null
    if (url !== this.currentPartUrl) {
      this.currentPartUrl = url
      this.clearPart()
      if (url) await this.loadPartGlb(url)
    }
    this.repaintFaceColors(job.operations)
    this.syncToolpaths(job.operations)
    await this.syncTool(job.cell.tool)
  }

  private async syncTool(tool: ToolDefinition | null) {
    const sig = tool
      ? `${tool.id}@${tool.version}:${tool.visual_url ?? ''}`
      : null
    if (sig === this.currentToolSig) return
    this.currentToolSig = sig

    // Clear previous tool mesh.
    while (this.toolGroup.children.length) {
      const c = this.toolGroup.children[0]
      this.toolGroup.remove(c)
      disposeTree(c)
    }
    if (!tool?.visual_url) return

    const loader = new GLTFLoader()
    try {
      const gltf = await loader.loadAsync(tool.visual_url)
      // Apply step_to_flange rotation so STEP modelled hanging in -Z
      // (WSATU-type) orients correctly, then flip 180° about X so the
      // wire direction (torch local +Z = out of flange face) ends up
      // pointing toward the part on the positioner instead of straight
      // up. At the robot's home pose the flange normal is world +X, but
      // for the first look we want the torch pointing roughly at the
      // part, which lives below the flange — the X-flip makes +Z → -Z
      // and keeps the result stable under either step_to_flange base.
      const q = tool.step_to_flange_quat_wxyz ?? [1, 0, 0, 0]
      const base = new THREE.Quaternion(q[1], q[2], q[3], q[0])
      const flip = new THREE.Quaternion(1, 0, 0, 0) // 180° about X
      gltf.scene.quaternion.copy(flip.multiply(base))
      // Paint the torch dark grey so it reads against the ABB orange.
      gltf.scene.traverse((obj) => {
        const m = obj as THREE.Mesh
        if (m.isMesh) {
          m.material = new THREE.MeshStandardMaterial({
            color: 0x4b5563,
            metalness: 0.4,
            roughness: 0.55,
          })
        }
      })
      this.toolGroup.add(gltf.scene)
    } catch (err) {
      console.warn('tool GLB load failed:', tool.visual_url, err)
    }
  }

  private clearPart() {
    while (this.partGroup.children.length) {
      const c = this.partGroup.children[0]
      this.partGroup.remove(c)
      disposeTree(c)
    }
    this.faceMeshes.clear()
  }

  private async loadPartGlb(url: string) {
    const loader = new GLTFLoader()
    try {
      const gltf = await loader.loadAsync(url)
      // Convention: each face is its own primitive/mesh in the GLB.
      // The backend tags userData.faceIndex via GLTF extras.
      gltf.scene.traverse((obj) => {
        if ((obj as THREE.Mesh).isMesh) {
          const mesh = obj as THREE.Mesh
          const extras = (mesh.userData as any).gltfExtensions?.AMATL_face_id
            ?? (mesh.userData as any).faceIndex
            ?? (mesh.userData as any).face_id
          const faceIdx = typeof extras === 'number' ? extras : parseInt(mesh.name.replace(/\D/g, ''), 10)
          if (Number.isFinite(faceIdx)) {
            mesh.userData.faceIndex = faceIdx
            mesh.material = new THREE.MeshStandardMaterial({
              color: 0xa0a8b4,
              metalness: 0.1,
              roughness: 0.6,
              transparent: true,
              opacity: 0.9,
            })
            this.faceMeshes.set(faceIdx, mesh)
          }
        }
      })
      this.partGroup.add(gltf.scene)
      // Orient first (rotation applied to the group BEFORE centering,
      // so the bounding-box centre is computed from the already-rotated
      // mesh). Then translate to the faceplate position.
      this.applyPartOrientation()
      const [fx, fy, fz] = this.faceplatePositionInRcf()
      this.centerPartOn(fx, fy, fz)
      this.zoomToFit()
    } catch (err) {
      console.error('GLB load failed', err)
    }
  }

  private centerPartOn(x: number, y: number, z: number) {
    const box = new THREE.Box3().setFromObject(this.partGroup)
    if (box.isEmpty()) return
    const c = box.getCenter(new THREE.Vector3())
    this.partGroup.position.set(x - c.x, y - c.y, z - c.z)
  }

  private repaintFaceColors(ops: Operation[]) {
    const selected = new Set<number>()
    const seamed = new Set<number>()
    for (const op of ops) {
      if (op.suppressed) continue
      if (op.kind === 'clad') op.faces.forEach((f) => selected.add(f))
      else if (op.kind === 'seam') {
        for (const p of op.seamPairs) {
          seamed.add(p.a)
          seamed.add(p.b)
        }
      }
    }
    for (const [idx, mesh] of this.faceMeshes) {
      const mat = mesh.material as THREE.MeshStandardMaterial
      if (selected.has(idx)) mat.color.setHex(0x3b82f6)
      else if (seamed.has(idx)) mat.color.setHex(0x22c55e)
      else mat.color.setHex(0xa0a8b4)
      mat.opacity = selected.has(idx) || seamed.has(idx) ? 0.95 : 0.35
    }
  }

  private syncToolpaths(ops: Operation[]) {
    while (this.pathGroup.children.length) {
      const c = this.pathGroup.children[0]
      this.pathGroup.remove(c)
      disposeTree(c)
    }
    const palette = [0x3b82f6, 0x22c55e, 0xf59e0b, 0xef4444, 0x8b5cf6, 0x14b8a6]
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i]
      const paths = extractPaths(op.toolpath)
      if (!paths.length) continue
      const color = palette[i % palette.length]
      for (const pts of paths) {
        if (pts.length < 2) continue
        const geom = new THREE.BufferGeometry().setFromPoints(
          pts.map(([x, y, z]) => new THREE.Vector3(x, y, z)),
        )
        this.pathGroup.add(new THREE.Line(geom, new THREE.LineBasicMaterial({ color })))
        // Arrow glyph every ~16 waypoints so direction is readable.
        for (let k = 16; k < pts.length; k += 16) {
          const a = new THREE.Vector3(...pts[k - 1])
          const b = new THREE.Vector3(...pts[k])
          const dir = b.clone().sub(a).normalize()
          this.pathGroup.add(new THREE.ArrowHelper(dir, a, 8, color, 4, 2))
        }
      }
    }
  }

  setFrame(_i: number) {
    // Phase 5 drives IK poses into the robot links. The viewport is
    // ready for it — just needs the robot GLB and per-frame transform
    // application.
  }
}

// ---- helpers ------------------------------------------------------------

function normalize3(v: number[]): [number, number, number] {
  const [x, y, z] = [v[0] ?? 0, v[1] ?? 0, v[2] ?? 0]
  const n = Math.hypot(x, y, z)
  if (n < 1e-9) return [1, 0, 0] // fall back to +X; keeps the geometry sane
  return [x / n, y / n, z / n]
}

function disposeTree(obj: THREE.Object3D) {
  obj.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.isMesh) {
      m.geometry?.dispose()
      const mat = m.material as THREE.Material | THREE.Material[]
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose())
      else mat?.dispose()
    }
  })
}

/**
 * Pull a list of (x,y,z)[] polylines out of the toolpath blob returned by
 * /api/generate. The amatl_core Toolpath carries `paths: PathOutput[]`,
 * and each PathOutput has `waypoints: Waypoint[]`; a Waypoint has `pose`
 * which has `point: [x,y,z]`. We tolerate both camelCase and snake_case.
 */
function extractPaths(toolpath: unknown): number[][][] {
  if (!toolpath || typeof toolpath !== 'object') return []
  const t = toolpath as any
  const paths = t.paths ?? []
  const out: number[][][] = []
  for (const p of paths) {
    const wps = p.waypoints ?? []
    const pts: number[][] = []
    for (const w of wps) {
      const pose = w.pose ?? w
      const pt = pose.point ?? pose.position ?? pose.p
      if (Array.isArray(pt) && pt.length === 3) {
        pts.push([Number(pt[0]), Number(pt[1]), Number(pt[2])])
      }
    }
    if (pts.length) out.push(pts)
  }
  return out
}
